import React, { useState, useEffect } from 'react';
import { vendorAPI } from '../services/api';
import { formatAmount } from '../utils/currency';
import { FiPackage, FiChevronDown, FiChevronUp, FiChevronLeft, FiAlertTriangle, FiCheck, FiClock, FiCalendar } from 'react-icons/fi';
import { useTone } from '../contexts/ToneContext';
import { format } from 'date-fns';

/**
 * System Payables Summary Component
 * ENTERPRISE RULE: Shows outstanding dues recorded under System Vendor (miscellaneous payables)
 * - Total outstanding for non-vendor payables
 * - Category-wise breakdown
 * - Transaction-level detail view with status and overdue indicators
 * - Separate from regular vendor dues
 */
const SystemPayablesSummary = () => {
  const { toneEnabled } = useTone();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryTransactions, setCategoryTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    fetchSystemPayables();
  }, []);

  const fetchSystemPayables = async () => {
    try {
      const response = await vendorAPI.getSystemPayables(false);
      if (response.data?.success) {
        setData(response.data.data);
      }
    } catch (error) {
      // Silently fail - this is optional data
      console.log('System payables fetch skipped:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTransactions = async (category) => {
    setLoadingTransactions(true);
    setSelectedCategory(category);
    try {
      const response = await vendorAPI.getSystemPayables(true, category.category_id);
      if (response.data?.success) {
        setCategoryTransactions(response.data.data.transactions || []);
      }
    } catch (error) {
      console.log('Failed to fetch category transactions:', error.message);
      setCategoryTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setCategoryTransactions([]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (txn) => {
    if (txn.transaction_type !== 'payable' && txn.transaction_type !== 'receivable') {
      return null;
    }

    const isPaid = txn.payment_status === 'paid';
    const isOverdue = txn.is_overdue;

    if (isPaid) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: '0.7rem',
          fontWeight: 500,
          backgroundColor: '#D4EDDA',
          color: '#155724',
        }}>
          <FiCheck size={12} />
          {toneEnabled ? 'Chuka diya' : 'Paid'}
        </span>
      );
    }

    if (isOverdue) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: '0.7rem',
          fontWeight: 500,
          backgroundColor: '#F8D7DA',
          color: '#721C24',
        }}>
          <FiAlertTriangle size={12} />
          {toneEnabled ? 'Late!' : 'Overdue'}
        </span>
      );
    }

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: '0.7rem',
        fontWeight: 500,
        backgroundColor: '#FFF3CD',
        color: '#856404',
      }}>
        <FiClock size={12} />
        {toneEnabled ? 'Baaki' : 'Pending'}
      </span>
    );
  };

  // Don't render if no data or no outstanding amounts
  if (loading || !data) return null;

  const { summary, categories } = data;
  const hasOutstanding = summary.pending_payable > 0 || summary.pending_receivable > 0;

  if (!hasOutstanding) return null;

  // Filter categories with outstanding amounts
  const outstandingCategories = categories.filter(
    cat => cat.pending_payable > 0 || cat.pending_receivable > 0
  );

  return (
    <div className="system-payables-summary" style={{
      backgroundColor: '#FFF9E6',
      border: '1px solid #FFE082',
      borderRadius: 8,
      padding: 16,
      marginBottom: 16,
    }}>
      {/* Header - always visible */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: selectedCategory ? 'default' : 'pointer',
        }}
        onClick={() => !selectedCategory && setExpanded(!expanded)}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', color: '#856404', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiPackage />
            {toneEnabled ? 'Farangi Udhari (Bina Dukandaar)' : 'System / Miscellaneous Payables'}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#7F8C8D' }}>
            {toneEnabled
              ? 'Woh udhari jo kisi vendor ke naam nahi hai'
              : 'Outstanding dues not tied to specific vendors'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#856404' }}>
            {formatAmount(summary.pending_payable)}
          </div>
          {summary.pending_receivable > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#3498DB' }}>
              {toneEnabled ? 'Lena hai' : 'Due from others'}: {formatAmount(summary.pending_receivable)}
            </div>
          )}
          {!selectedCategory && (expanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />)}
        </div>
      </div>

      {/* Category List View */}
      {expanded && !selectedCategory && outstandingCategories.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid #FFE082', paddingTop: 12 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#856404' }}>
            {toneEnabled ? 'Category-wise Udhari' : 'Category Breakdown'}
            <span style={{ fontSize: '0.75rem', fontWeight: 'normal', marginLeft: 8, color: '#7F8C8D' }}>
              ({toneEnabled ? 'Click karo details ke liye' : 'Click for details'})
            </span>
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {outstandingCategories.map((cat, idx) => (
              <div
                key={cat.category_id || idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  backgroundColor: '#FFFEF5',
                  borderRadius: 4,
                  border: '1px solid #FFF3CD',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => fetchCategoryTransactions(cat)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FFF8E1'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFEF5'}
              >
                <div>
                  <span style={{ fontWeight: 500 }}>{cat.category_name}</span>
                  <span style={{ fontSize: '0.75rem', color: '#7F8C8D', marginLeft: 8 }}>
                    ({cat.transaction_count} {cat.transaction_count === 1 ? 'entry' : 'entries'})
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ textAlign: 'right' }}>
                    {cat.pending_payable > 0 && (
                      <div style={{ color: '#F39C12', fontWeight: 500 }}>
                        {toneEnabled ? 'Dena' : 'Owe'}: {formatAmount(cat.pending_payable)}
                      </div>
                    )}
                    {cat.pending_receivable > 0 && (
                      <div style={{ color: '#3498DB', fontWeight: 500 }}>
                        {toneEnabled ? 'Lena' : 'Due'}: {formatAmount(cat.pending_receivable)}
                      </div>
                    )}
                  </div>
                  <FiChevronDown size={16} style={{ color: '#7F8C8D', transform: 'rotate(-90deg)' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction Detail View */}
      {selectedCategory && (
        <div style={{ marginTop: 16, borderTop: '1px solid #FFE082', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button
              onClick={handleBackToCategories}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                border: '1px solid #FFE082',
                borderRadius: 4,
                backgroundColor: 'transparent',
                color: '#856404',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              <FiChevronLeft size={16} />
              {toneEnabled ? 'Wapas' : 'Back'}
            </button>
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#856404' }}>
              {selectedCategory.category_name}
            </h4>
          </div>

          {loadingTransactions ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#7F8C8D' }}>
              {toneEnabled ? 'Load ho raha hai...' : 'Loading...'}
            </div>
          ) : categoryTransactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#7F8C8D', fontStyle: 'italic' }}>
              {toneEnabled ? 'Koi entry nahi mili' : 'No entries found'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {categoryTransactions.map((txn) => (
                <div
                  key={txn.id}
                  style={{
                    padding: '12px',
                    backgroundColor: txn.is_overdue ? '#FFF5F5' : '#FFFEF5',
                    borderRadius: 4,
                    border: `1px solid ${txn.is_overdue ? '#F5C6CB' : '#FFF3CD'}`,
                  }}
                >
                  {/* Row 1: Date, Status, Amount */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FiCalendar size={14} style={{ color: '#7F8C8D' }} />
                      <span style={{ fontSize: '0.85rem', color: '#555' }}>
                        {formatDate(txn.transaction_date)}
                      </span>
                      {getStatusBadge(txn)}
                    </div>
                    <div style={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: txn.transaction_type === 'payable' ? '#F39C12' : '#3498DB',
                    }}>
                      {txn.transaction_type === 'payable' ? '-' : '+'}{formatAmount(txn.amount)}
                    </div>
                  </div>

                  {/* Row 2: Description */}
                  <div style={{ fontSize: '0.9rem', color: '#333', marginBottom: txn.commitment_date ? 4 : 0 }}>
                    {txn.description || txn.notes || (toneEnabled ? 'Koi description nahi' : 'No description')}
                  </div>

                  {/* Row 3: Due Date (if exists) */}
                  {txn.commitment_date && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: '0.8rem',
                      color: txn.is_overdue ? '#DC3545' : '#7F8C8D',
                      marginTop: 4,
                    }}>
                      <FiClock size={12} />
                      {toneEnabled ? 'Due date' : 'Due'}: {formatDate(txn.commitment_date)}
                      {txn.is_overdue && (
                        <span style={{ fontWeight: 500, marginLeft: 4 }}>
                          ({toneEnabled ? 'Late ho gaya!' : 'Overdue!'})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Row 4: Transaction Type Label */}
                  <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 6 }}>
                    {txn.transaction_type === 'payable'
                      ? (toneEnabled ? 'Dena hai' : 'Payable')
                      : txn.transaction_type === 'receivable'
                      ? (toneEnabled ? 'Lena hai' : 'Receivable')
                      : txn.transaction_type === 'expense_paid' || txn.transaction_type === 'expense'
                      ? (toneEnabled ? 'Kharch kiya' : 'Expense')
                      : (toneEnabled ? 'Aaya' : 'Income')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {expanded && !selectedCategory && outstandingCategories.length === 0 && (
        <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#7F8C8D', fontStyle: 'italic' }}>
          {toneEnabled ? 'Koi category-wise data nahi hai' : 'No category breakdown available'}
        </div>
      )}
    </div>
  );
};

export default SystemPayablesSummary;
